import React from 'react';
import { Download } from 'lucide-react';

import { opsVisibilityApi } from 'api/opsVisibility';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';
import { downloadCSV } from 'shared/components/csvDownload';

/**
 * ProductTable
 *
 * Renders a table of OOB manifest items with per-row detailed CSV export
 * and a full summary CSV download button.
 *
 * @param {Object} props
 * @param {Object} props.manifest - OOB manifest object
 * @param {string} props.manifest.line_haul_id - Line haul ID
 * @param {string} props.manifest.expected_pack_date - Pack date
 * @param {Array} props.manifest.items - Array of { externalSku, skuName, route, totalQty }
 */
function ProductTable({ manifest }) {
    const { items = [] } = manifest || {};

    if (!items.length) {
        return <p className="text-sm text-muted-foreground py-4 text-center">No items to prep for this manifest.</p>;
    }

    const handleDownloadSummaryCSV = () => {
        const headers = ['External SKU', 'Product Name', 'Route', 'Total Qty'];
        let csvContent = `${headers.join(',')}\r\n`;

        items.forEach((item) => {
            const row = [
                `"${(item.externalSku || '').replace(/"/g, '""')}"`,
                `"${(item.skuName || '').replace(/"/g, '""')}"`,
                `"${(item.route || '').replace(/"/g, '""')}"`,
                item.totalQty || 0,
            ];
            csvContent += `${row.join(',')}\r\n`;
        });

        const filename = `OOB_Manifest_${manifest.line_haul_id}_${manifest.expected_pack_date}.csv`;
        downloadCSV(csvContent, filename);
    };

    const handleDownloadDetailedCSV = async (route) => {
        try {
            await opsVisibilityApi.downloadOutOfBoxManifestByRouteCsv({
                lineHaulId: manifest.line_haul_id,
                expectedPackDate: manifest.expected_pack_date,
                route,
            });
        } catch {
            // Download failures are surfaced by the browser's network layer
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Items to Prep</span>
                    <Badge variant="secondary">{items.length} unique</Badge>
                </div>
                <Button
                    onClick={handleDownloadSummaryCSV}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1"
                >
                    <Download className="h-3.5 w-3.5" />
                    Summary CSV
                </Button>
            </div>

            <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b bg-muted/50">
                            <th className="px-3 py-2 text-left font-medium">External SKU</th>
                            <th className="px-3 py-2 text-left font-medium">Product Name</th>
                            <th className="px-3 py-2 text-left font-medium">Route</th>
                            <th className="px-3 py-2 text-right font-medium">Total Qty</th>
                            <th className="px-3 py-2 text-right font-medium">Export</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {items.map((item) => (
                            <tr key={`${item.externalSku}-${item.route}`} className="hover:bg-muted/30">
                                <td className="px-3 py-2 font-medium">{item.externalSku}</td>
                                <td className="px-3 py-2 text-muted-foreground">{item.skuName}</td>
                                <td className="px-3 py-2">
                                    <Badge variant="outline">{item.route}</Badge>
                                </td>
                                <td className="px-3 py-2 text-right font-medium">{item.totalQty}</td>
                                <td className="px-3 py-2 text-right">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDownloadDetailedCSV(item.route)}
                                        className="h-7 px-2"
                                    >
                                        <Download className="h-3.5 w-3.5" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default ProductTable;
